import EditableList from '@/components/EditableList'
import RelayCard from '@/components/RelayCard'
import { useFetchProfile } from '@/hooks/useFetchProfile'
import { normalizeUrl } from '@/lib/url'
import { username } from '@/lib/event-metadata'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { useAtomValue } from 'jotai'
import { invalidateSearchRelays, searchRelaysChannelAtom } from '@/atoms/invalidation'
import client from '@/services/client.service'
import { loadSearchRelays } from '@nostr/gadgets/lists'
import { unwrappedSearchRelaysAtom } from '@/atoms/lists'
import * as kinds from '@nostr/tools/kinds'
import { now } from '@/lib/utils'
import { forwardRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAsync } from 'react-use'
import { useCurrentPubkey } from '@/atoms/session'
import { sampleTopFromFollows } from '@/lib/recommendations'

const SearchRelaysPage = forwardRef(({ id, index }: { id: string; index?: number }, ref) => {
  const { t } = useTranslation()
  const currentPubkey = useCurrentPubkey()
  const { profile } = useFetchProfile(id)
  const channel = useAtomValue(searchRelaysChannelAtom)
  const ourSearchRelays = useAtomValue(unwrappedSearchRelaysAtom) ?? []

  useEffect(() => {
    if (!profile) return
    const name = profile.shortName || username(profile) || '...'
    document.title = t("{{name}}'s search relays on {{appName}}", {
      name,
      appName: window.hallway.universe.appName
    })
    history.replaceState(null, '')
  }, [profile])
  const { value: relays } = useAsync(async () => {
    if (!profile) return []
    if (profile.pubkey === currentPubkey) return ourSearchRelays
    return (await loadSearchRelays(profile.pubkey)).items
  }, [profile?.pubkey, channel, ourSearchRelays])

  async function loadRecommendations(): Promise<string[]> {
    if (!currentPubkey || !relays) return []
    const existing = new Set(relays.map(normalizeUrl))
    const fromFollows = await sampleTopFromFollows<string>(
      currentPubkey,
      async (pk) => (await loadSearchRelays(pk)).items,
      (url) => normalizeUrl(url),
      existing
    )
    const universe = window.hallway.universe.defaultFavoriteRelays
      .map(normalizeUrl)
      .filter((u) => !existing.has(u) && !fromFollows.includes(u))
    return [...fromFollows, ...universe]
  }

  return (
    <SecondaryPageLayout
      ref={ref}
      index={index}
      title={
        profile && profile.pubkey !== currentPubkey
          ? t("{{name}}'s search relays", { username: username(profile) })
          : t('Search Relays')
      }
    >
      <div className="px-4 py-3">
        <EditableList
          items={relays || []}
          editable={profile?.pubkey === currentPubkey}
          renderItem={(url) => <RelayCard url={url} />}
          validateItem={(val: string) => {
            if (!currentPubkey) return { ok: false as const, error: t('Login required') }
            const normalized = normalizeUrl(val)
            if (!normalized) return { ok: false as const, error: t('Invalid relay URL') }
            if ((relays || []).includes(normalized))
              return { ok: false as const, error: t('Relay already exists') }
            return { ok: true as const, item: normalized }
          }}
          onUpdate={async (newRelays: string[]) => {
            if (!currentPubkey) return
            const event = await client.publish({
              kind: kinds.SearchRelaysList,
              content: '',
              tags: newRelays.map((url) => ['relay', url]),
              created_at: now()
            })
            if (!event) {
              console.warn('failed to publish updated search relays list')
              toast.error(t('Failed to publish updated search relays list.'))
              return
            }
            await loadSearchRelays(event.pubkey, undefined, event)
            invalidateSearchRelays()
          }}
          placeholder={t('Enter relay URL')}
          emptyMessage={t('No search relays configured.')}
          loadRecommendations={loadRecommendations}
        />
      </div>
    </SecondaryPageLayout>
  )
})
SearchRelaysPage.displayName = 'SearchRelaysPage'
export default SearchRelaysPage
